import React, { ReactElement, useMemo, useCallback } from "react";
import {
    QualityHubTaskDetailsProps,
    QualityHubTaskDetailsStateProps,
} from "./QualityHubTaskDetails.types";
import { SmallBody, MediumBodyBolder, CaptionBody } from "src/components/Text";
import { UnstyledButton } from "src/ui";
import { localize, localizeFormat } from "src/l10n";
import { Conditional } from "src/components/Conditional";
import { useSelector } from "react-redux";
import { SpintrTypes } from "src/typings";
import moment from "moment";

/**
 * Renders the QualityHubTaskDetails component.
 *
 * @param {QualityHubTaskDetailsProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function QualityHubTaskDetails({
    canCreateAction = true,
    onCreateActionClick,
    task,
}: QualityHubTaskDetailsProps): ReactElement | null {
    const l10n = useMemo(() => ({
        createAction: localize("QH_CREATE_ACTION"),
    }), []);

    const onCreateActionClicked = useCallback(
        () => onCreateActionClick?.(task),
        [onCreateActionClick, task],
    );

    const {
        followUpList,
        taskList,
    } = useSelector<Spintr.AppState, QualityHubTaskDetailsStateProps>(
        (appState) => ({
            followUpList: !task?.followUpFrom?.listId
                ? undefined
                : appState.qualityHub.listsById[task.followUpFrom.listId],
            taskList: appState.qualityHub.listsById[task.listId],
        }),
    );

    return (
        <div className="QualityHubTaskDetails">
            <Conditional condition={task?.followUpFrom?.title.length > 0}>
                <CaptionBody>
                    {localizeFormat("QH_FOLLOW_UP_FROM_X", task?.followUpFrom?.title)}
                    {followUpList && ` (${followUpList?.name})`}
                </CaptionBody>
            </Conditional>
            <Conditional condition={taskList?.listType === SpintrTypes.TaskListType.ActionList}>
                <CaptionBody>
                    {localize("Skapad")}: {moment(task.createdOn).format("YYYY-MM-DD")}
                </CaptionBody>
            </Conditional>
            <SmallBody as="div"
                       className="QualityHubTaskDetails-description"
                       color="contentNormal">
                {task?.description}
            </SmallBody>
            <Conditional condition={!!onCreateActionClick && canCreateAction}>
                <div className="QualityHubTaskDetails-actions">
                    <UnstyledButton className="QualityHubTaskDetails-createAction"
                                    onClick={onCreateActionClicked}>
                        <MediumBodyBolder color="blue">
                            {l10n.createAction}
                        </MediumBodyBolder>
                    </UnstyledButton>
                </div>
            </Conditional>
            <Conditional condition={task.images && task.images.length > 0}>
                <div className="QualityHubTaskDetails-files">
                    {task.images.map((image) => (
                        <img key={image.id}
                             className="QualityHubTaskDetails-image"
                             src={image.imageUrl} />
                    ))}
                </div>
            </Conditional>
        </div>
    );
}

export default QualityHubTaskDetails;

